/**
 * The Financial Structures route table (FSD 1.2.7 — Chart of Accounts half).
 *
 * `web/src/router/index.ts` and `web/src/nav/parts/p01-enterprise.ts` are
 * shared files sibling C14 packets also touch and this packet does NOT own —
 * the orchestrator spreads `FINANCIAL_STRUCTURES_ROUTES` into the router and
 * adds the nav entry once every packet in this batch has landed (see
 * `web/src/modules/enterprise/legal-entities/routes.ts`'s identical note).
 *
 * Every route is gated on the matching **read** permission so Internal Audit
 * (FSD 1.2.7.1) can open every screen here and change nothing.
 */

import type { RouteRecordRaw } from 'vue-router'

import { COA_PERMISSIONS } from './permissions'

export const FINANCIAL_STRUCTURES_BASE = '/enterprise/financial-structures'
export const VALUE_SETS_BASE = '/enterprise/financial-structures/value-sets'

export function financialStructuresRoute(): string {
  return FINANCIAL_STRUCTURES_BASE
}

export function financialStructureRoute(id: number): string {
  return `${FINANCIAL_STRUCTURES_BASE}/${id}`
}

export function valueSetsRoute(): string {
  return VALUE_SETS_BASE
}

export function valueSetRoute(id: number): string {
  return `${VALUE_SETS_BASE}/${id}`
}

export const CVR_REGISTER_PATH = '/enterprise/financial-structures/cross-validation-rules'

export function cvrRegisterRoute(): string {
  return CVR_REGISTER_PATH
}

export const FINANCIAL_STRUCTURES_ROUTES: RouteRecordRaw[] = [
  {
    path: FINANCIAL_STRUCTURES_BASE,
    name: 'enterprise-financial-structures',
    component: () => import('./StructuresListView.vue'),
    meta: {
      titleKey: 'enterprise.financialStructures.routeTitles.list',
      permission: COA_PERMISSIONS.structureRead,
    },
  },
  // Static children must be declared before the dynamic `:id` route below, or
  // vue-router would try to parse "value-sets" as a structure id.
  {
    path: VALUE_SETS_BASE,
    name: 'enterprise-financial-structures-value-sets',
    component: () => import('./ValueSetsListView.vue'),
    meta: {
      titleKey: 'enterprise.financialStructures.routeTitles.valueSetsList',
      permission: COA_PERMISSIONS.valueSetRead,
    },
  },
  {
    // FSD 1.2.7 cross-validation rule register, across every structure. Static,
    // so it too sits before the dynamic `:id` route.
    path: CVR_REGISTER_PATH,
    name: 'enterprise-cvr-register',
    component: () => import('./CvrRegisterView.vue'),
    meta: {
      titleKey: 'enterprise.financialStructures.routeTitles.cvrRegister',
      permission: COA_PERMISSIONS.ruleRead,
    },
  },
  {
    path: `${VALUE_SETS_BASE}/:id(\\d+)`,
    name: 'enterprise-financial-structures-value-set',
    component: () => import('./ValueSetDetailView.vue'),
    meta: {
      titleKey: 'enterprise.financialStructures.routeTitles.valueSetDetail',
      permission: COA_PERMISSIONS.valueSetRead,
    },
  },
  {
    path: `${FINANCIAL_STRUCTURES_BASE}/:id(\\d+)`,
    name: 'enterprise-financial-structure',
    component: () => import('./StructureDetailView.vue'),
    meta: {
      titleKey: 'enterprise.financialStructures.routeTitles.detail',
      permission: COA_PERMISSIONS.structureRead,
    },
  },
]
